import * as React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface DisabledHintTooltipProps {
  /** The disabled control(s) to wrap. */
  children: React.ReactNode;
  /** When true (and `hint` is set), wraps `children` in a hover tooltip. */
  disabled: boolean;
  /** Tooltip text shown on hover while disabled. */
  hint?: string;
  /** Tooltip open delay in ms. Defaults to 300. */
  delayDuration?: number;
}

/**
 * Wraps a disabled control in a hover tooltip explaining why it's disabled,
 * instead of hiding it. No-op (returns `children` as-is) when not disabled
 * or no `hint` is given.
 *
 * A `span[tabIndex=0]` wrapper is required around the disabled child: native
 * `disabled` elements have `pointer-events: none` and never fire the hover
 * events the tooltip trigger depends on.
 */
export function DisabledHintTooltip({
  children,
  disabled,
  hint,
  delayDuration = 300,
}: DisabledHintTooltipProps): React.ReactNode {
  if (!disabled || hint == null) return children;
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{children}</span>
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
